/** ************************************************************************
 * Atomic Component for the bPerx iFrame UI.
 *
 * Atomic Type: Offer Display Product, Individual Product
 * Purpose: Display an individual product.
 *
 * This product represents whatever product is being offered in the offer.
 * The original product in 2.0.0 was magazines, but there are plans to expand
 * the types of products being offered.
 *
 * Note the behavior on "clicking" a product - if a user selects a product
 * while they are opted out, they will be opted back in.
 *
 * @file   Atomic Component, offer display, individual product
 * @author Paul J Stales
 * @since  2.2.0
 ************************************************************************ */

import { FunctionalComponent, h } from 'preact'
import { ProductMagazine } from '../../../../../../common/types/productTypes/ProductMagazine'
import { TransactionInformation } from '../../../../../../common/types/transactionInformationTypes/TransactionInformation'
import { css } from 'aphrodite'
import { IFRAME_CLASSNAMES } from '../../../../../../common/constants/ConstantsIFrameClassNames'
import { DisplayEvent } from '../../../../../../common/types/reportableEvents/subTypes/DisplayEvent'
import { ReportableEventType } from '../../../../../../common/types/reportableEvents/ReportableEvent'

/**
 * Props Interface for Product
 */
interface ProductProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  styles: { [key: string]: any }
  product: ProductMagazine
  transactionInformation: TransactionInformation
  setTransactionInformation: (
    transactionInformation: TransactionInformation
  ) => void
  setDoesPartnerNeedUpdateForTransactionInformation: (b: boolean) => void
  areProductsVisible: boolean
  setProductsVisible: (areProductsVisible: boolean) => void
  displayEvent: DisplayEvent
  setDisplayEvent: (displayEvent: DisplayEvent) => void
  displayEventHandler: (
    displayEvent: DisplayEvent,
    setDisplayEvent: (displayEvent: DisplayEvent) => void,
    newTransactionInformation: TransactionInformation,
    reportableEventType: string
  ) => void
}

/**
 * Component "product" represents a single offer item.
 *
 * The original product of bPerx iFrame was magazines, but
 * the future may hold additional types of products.
 *
 * @param ProductProps
 * @returns
 */
const Product: FunctionalComponent<ProductProps> = ({
  styles,
  product,
  transactionInformation,
  setTransactionInformation,
  setDoesPartnerNeedUpdateForTransactionInformation,
  areProductsVisible,
  setProductsVisible,
  displayEvent,
  setDisplayEvent,
  displayEventHandler
}) => {
  return (
    <div
      class={`${IFRAME_CLASSNAMES.BPERX_PRODUCT_ITEM_CONTAINER}${' '}${
        styles[IFRAME_CLASSNAMES.BPERX_PRODUCT_ITEM_CONTAINER] === undefined
          ? ''
          : css(styles[IFRAME_CLASSNAMES.BPERX_PRODUCT_ITEM_CONTAINER])
      }`}
    >
      <img
        src={product.coverImageURL}
        alt={`Cover image of ' + ${product.title}`}
        class={`${IFRAME_CLASSNAMES.BPERX_PRODUCT_ITEM_IMAGE}${' '}${
          styles[IFRAME_CLASSNAMES.BPERX_PRODUCT_ITEM_IMAGE] === undefined
            ? ''
            : css(styles[IFRAME_CLASSNAMES.BPERX_PRODUCT_ITEM_IMAGE])
        }`}
        onClick={(e) => {
          setProductsVisible(!areProductsVisible)

          const newTransactionInformation = transactionInformationHandler(
            transactionInformation,
            product,
            setTransactionInformation,
            setDoesPartnerNeedUpdateForTransactionInformation
          )

          displayEventHandler(
            displayEvent,
            setDisplayEvent,
            newTransactionInformation,
            ReportableEventType.DISPLAY_CUSTOMER_PRODUCT_SELECT_EVENT
          )
        }}
      />
      <p
        class={`${IFRAME_CLASSNAMES.BPERX_PRODUCT_ITEM_TITLE}${' '}${
          styles[IFRAME_CLASSNAMES.BPERX_PRODUCT_ITEM_TITLE] === undefined
            ? ''
            : css(styles[IFRAME_CLASSNAMES.BPERX_PRODUCT_ITEM_TITLE])
        }`}
      >
        {product.title}
      </p>
    </div>
  )
}

export default Product

/**
 * Create a new TransactionInformation with the changes to opt status and product selected,
 * then execute setTransactionInformation useState update function.
 *
 * There is a useEffect triggered by this that will update the
 * parent/partner window about the updated TransactionInformation.
 *
 * @param transactionInformation
 * @param product
 * @param setTransactionInformation
 * @param setDoesPartnerNeedUpdateForTransactionInformation
 */
function transactionInformationHandler(
  transactionInformation: TransactionInformation,
  product: ProductMagazine,
  setTransactionInformation: (
    transactionInformation: TransactionInformation
  ) => void,
  setDoesPartnerNeedUpdateForTransactionInformation: (b: boolean) => void
): TransactionInformation {
  const NEW_TRANSACTION_INFORMATION = {
    ...transactionInformation
  }
  NEW_TRANSACTION_INFORMATION.customerChoice.isOptingIn = true
  NEW_TRANSACTION_INFORMATION.customerChoice.productMagazineChosen = product
  setTransactionInformation(NEW_TRANSACTION_INFORMATION)
  setDoesPartnerNeedUpdateForTransactionInformation(true)

  return NEW_TRANSACTION_INFORMATION
}
